import type { StrapiImage, StrapiFaq } from '~/composables/types/strapi/common'
import type {
  StrapiMagazineArticle,
  StrapiMagazineArticleCategory,
} from '~/composables/types/strapi/strapi-magazine'

const getId = (article: StrapiMagazineArticle): string | null => {
  return article?.id || null
}

const getSlug = (article: StrapiMagazineArticle): string | null => {
  return article?.slug || null
}

const getHeaderImage = (article: StrapiMagazineArticle): StrapiImage | null => {
  return article?.header_image || null
}

const getHeaderImageUrl = (
  article: StrapiMagazineArticle,
  format = null
): string | null => {
  return strapiImageGetters.getImageUrl(getHeaderImage(article), format) || null
}

const getHeaderImageAlt = (article: StrapiMagazineArticle): string => {
  return strapiImageGetters.getAlternativeText(getHeaderImage(article)) || ''
}

const getTitle = (article: StrapiMagazineArticle): string | null => {
  return article?.title || null
}

const getContent = (article: StrapiMagazineArticle): string | null => {
  return article?.content || null
}

const getDescription = (article: StrapiMagazineArticle): string | null => {
  return article?.description || null
}

const getSeoTitle = (article: StrapiMagazineArticle): string | null => {
  return article?.seo?.metaTitle || null
}

const getSeoImageUrl = (
  article: StrapiMagazineArticle,
  format = null
): string | null => {
  return (
    strapiImageGetters.getImageUrl(article?.seo?.metaImage?.data, format) ||
    null
  )
}

const getSeoMetaDescription = (
  article: StrapiMagazineArticle
): string | null => {
  return article?.seo?.metaDescription || null
}

const getCreatedAt = (article: StrapiMagazineArticle): string | null => {
  return article?.createdAt || null
}

const getUpdatedAt = (article: StrapiMagazineArticle): string | null => {
  return article?.updatedAt || null
}

const getAuthor = (article: StrapiMagazineArticle): string => {
  if (article?.external_author_name) {
    return article?.external_author_name
  }

  const adminUserAttributes = article?.admin_user?.data || ''
  return (
    `${adminUserAttributes?.firstname || ''} ${
      adminUserAttributes?.lastname || ''
    }` || ''
  )
}

const getAuthorUrl = (article: StrapiMagazineArticle): string | null => {
  return article?.author_url || null
}

const getSummaryList = (article: StrapiMagazineArticle): any => {
  return article?.summary_list || null
}

const getCategories = (
  article: StrapiMagazineArticle
): StrapiMagazineArticleCategory[] | null => {
  return article?.categories || null
}

const getFirstCategory = (
  article: StrapiMagazineArticle
): StrapiMagazineArticleCategory | null => {
  const categoriesArr = getCategories(article)
  return categoriesArr && categoriesArr.length > 0 ? categoriesArr[0] : null
}

const getEventPermalink = (article: StrapiMagazineArticle): string | null => {
  return article?.event_permalink || null
}

const getSupplierId = (article: StrapiMagazineArticle): string | null => {
  return article?.supplier_id || null
}
const getFaq = (article: StrapiMagazineArticle): StrapiFaq | null => {
  return article?.faq || null
}

const getArticleShowcaseArticles = (
  article: StrapiMagazineArticle
): StrapiMagazineArticle[] | null => {
  return article?.article_showcase?.articles || null
}

const getArticleShowcaseSectionTitle = (
  article: StrapiMagazineArticle
): string | null => {
  return article?.article_showcase?.section_title || null
}

const getAlertText = (article: StrapiMagazineArticle): string | null => {
  return article?.alert_text || null
}

const getArticleMetrics = (
  article: StrapiMagazineArticle
): { entity: string; value: string } => {
  return article?.metrics
}

export const articleGetters = {
  getId,
  getSlug,
  getHeaderImage,
  getHeaderImageUrl,
  getHeaderImageAlt,
  getTitle,
  getContent,
  getDescription,
  getSeoTitle,
  getSeoImageUrl,
  getSeoMetaDescription,
  getAuthor,
  getAuthorUrl,
  getCreatedAt,
  getUpdatedAt,
  getSummaryList,
  getCategories,
  getFirstCategory,
  getEventPermalink,
  getSupplierId,
  getFaq,
  getArticleShowcaseArticles,
  getArticleShowcaseSectionTitle,
  getAlertText,
  getArticleMetrics,
}
